import { initProductCarousels } from '../../sections/INF-ProductOverview/carousel';
import { destroyZoom } from '../ProductImages';
import { buildProductNodes, finalSaleCalculate } from '../../utils/finalSale';
import { fetchKlarnaData } from '../../utils/klarnaConfig';
import ConstructorTracking from '../ConstructorTracking';
import { Gallery } from '../../components/Gallery';
import { getSectionInnerHTML } from '../../sections/PDP/utils';
import { applyVideoFetchPrefix } from '../../utils/videoUrlPrefix';

// Set current variant barcode
const replaceVariantBarcode = (variant) => {
  const currentVariantElement =
    document.querySelector('[data-current-variant-barcode]') || document.createElement('div');
  currentVariantElement.setAttribute('data-current-variant-barcode', variant.barcode || variant.sku);
  if (!currentVariantElement.parentElement) {
    currentVariantElement.style.display = 'none';
    document.body.appendChild(currentVariantElement);
  }

  // Set the barcode on the ProductOptions container for easier access
  const productOptionsContainer = document.querySelector('.ProductOptions');
  if (productOptionsContainer) {
    productOptionsContainer.setAttribute('data-variant-barcode', variant.barcode || variant.sku);
  }
};

// update the style number under the title
const updateStyleNumber = (variant) => {
  const styleNumberValue = document.querySelector('[data-style-number-value]');
  if (styleNumberValue) {
    styleNumberValue.innerHTML = variant.barcode;
  }
};

// update size under the title
const updateSizeValue = (variant) => {
  const sizeVariantValue = document.querySelector('[data-size-variant-value]');
  const shoeSize = sizeVariantValue.getAttribute('data-size-shoe');
  if (sizeVariantValue) {
    sizeVariantValue.innerHTML = `${shoeSize == 'true' ? 'US ' : ''}${variant.option2}`;
  }
};

// update the color name
const updateColorValue = (variant) => {
  const colorVariantValue = document.querySelector('[data-color-variant-value]');
  if (colorVariantValue) {
    colorVariantValue.innerHTML = variant.option1;
  }
};

// Update low inventory message
const updateLowInvetoryMessage = (variant, inventory_json) => {
  const inventory = Number(inventory_json[variant.id]);
  const lowInventoryMessage = document.querySelector('[data-low-inventory-message]');
  if (lowInventoryMessage) {
    if (inventory < 10 && inventory >= 1) {
      lowInventoryMessage.textContent = 'Almost Gone';
      lowInventoryMessage.style.display = 'block';
    } else {
      lowInventoryMessage.style.display = 'none';
    }
  }
};

// Update Klarna widget value with a new price
const updateKlarnaPaymentValue = (variant) => {
  // Exit successfully for international user experiences
  if (window.theme?.country_iso !== 'US') return;

  const klarnaWrapper = document.querySelector('[data-klarna-osm]');
  if (!klarnaWrapper) return;

  // Get the parent wrapper
  const bnplWrapper = klarnaWrapper.closest('.pdp-c-atc-bnpl');

  // Hide the wrapper while loading
  if (bnplWrapper) {
    bnplWrapper.style.visibility = 'hidden';
  }

  // Get currency and price for Klarna key
  const currency = window.theme?.currency?.toLowerCase() || 'usd';
  const priceInCents = variant?.price || 0;
  const klarnaKey = `price_${currency}_${priceInCents}`;

  // Update the key attribute
  klarnaWrapper.setAttribute('data-klarna-key', klarnaKey);

  // Fetch Klarna data and populate
  fetchKlarnaData(klarnaKey)
    .then((data) => {
      if (data) {
        // Extract TEXT_MAIN and remove 'Klarna' from it
        let textMain = data.TEXT_MAIN || '';
        if (textMain) {
          textMain = textMain.replace(/Klarna/gi, '').trim();
          klarnaWrapper.setAttribute('data-klarna-text-main', textMain);
        }

        // Extract ACTION_LEARN_MORE (the URL)
        if (data.ACTION_LEARN_MORE) {
          klarnaWrapper.setAttribute('data-klarna-learn-more', 'Learn More');
          klarnaWrapper.setAttribute('data-klarna-learn-url', data.ACTION_LEARN_MORE);
        }

        // Update UI
        const textEl = klarnaWrapper.querySelector('[data-klarna-text-ui]');
        if (textEl) textEl.textContent = textMain;

        // Only show if we have all the necessary data (text and URL)
        if (textMain && data.ACTION_LEARN_MORE && bnplWrapper) {
          // Use a small delay to ensure smooth rendering
          requestAnimationFrame(() => {
            bnplWrapper.style.visibility = 'visible';
          });
        } else if (bnplWrapper) {
          bnplWrapper.style.visibility = 'hidden';
        }
      } else {
        // No data received, keep hidden
        if (bnplWrapper) {
          bnplWrapper.style.visibility = 'hidden';
        }
      }
    })
    .catch((err) => {
      // On error, keep hidden
      console.warn('[Klarna] Failed to update payment value:', err);
      if (bnplWrapper) {
        bnplWrapper.style.visibility = 'hidden';
      }
    });
};

// Turn off/on the clearance bagde
const switchClearanceBadge = (variant) => {
  const badgeClearance = document.querySelector('[data-badge-clearance]');
  if (badgeClearance) {
    if (variant.price >= variant.compare_at_price) {
      badgeClearance.style.display = 'none';
    } else {
      badgeClearance.style.display = 'block';
    }
  }
};

// Update SKU and Variant ID
const replaceSkuAndVariantId = (variant) => {
  const skuAttribute = document.querySelector('[data-sku]');
  if (skuAttribute) {
    skuAttribute.setAttribute('data-sku', variant.sku);
  }
  const variantIdAttribute = document.querySelector('[data-variant-id]');
  if (variantIdAttribute) {
    variantIdAttribute.setAttribute('data-variant-id', variant.id);
  }
};

// Turn off/on the final sale message
const toggleFinalSaleMessage = (variant, product_json) => {
  const variantFinalSaleMetafieldJson = JSON.parse(
    document.querySelector('[data-variant-scoped-final-sale-metafield-json]').innerHTML,
  );
  const productNodes = buildProductNodes([variant], product_json, variantFinalSaleMetafieldJson);

  finalSaleCalculate({
    isInternational: window.theme.country_iso !== 'US',
    productNodes,
  })
    .then((result) => {
      const variantResult = result.at(0);

      const showForClearance = document.querySelector('[data-show-for-clearance]');
      if (variantResult.finalSale) {
        showForClearance.style.display = 'block';
      } else {
        showForClearance.style.display = 'none';
      }
    })
    .catch((err) => {
      console.error(`Error while trying calculate final sale on pdp or quick shop: ${err}`);
    });
};

// Update sticky nav options and select values
// TODO: This might be removed in the future, it is no londer used
const syncStickyNavOptions = (variant) => {
  const container = document.querySelector('body'); // Adjust this if necessary to point to the correct container element
  const select1 = container.querySelector('[data-custom-option-selector="option1"] select');
  const select2 = container.querySelector('[data-custom-option-selector="option2"] select');

  if (select1) {
    select1.value = variant.option1;
  }

  if (select2) {
    select2.value = variant.option2;
  }
};

// Sync the sticky secondary nav options
// TODO: This might be removed in the future, it is no londer used
const syncStickySecondaryNavOptions = (variant) => {
  document
    .querySelectorAll('[data-secondary-color-variant-selector]')
    .forEach((el) => el.classList.remove('ProductOption__variant--selected'));
  document
    .querySelectorAll('[data-secondary-size-variant-selector]')
    .forEach((el) => el.classList.remove('ProductOption__variant--selected'));

  const secondaryColorSelector = document.querySelector(
    `[data-secondary-color-variant-selector][data-value="${variant.option1}"]`,
  );
  if (secondaryColorSelector) secondaryColorSelector.classList.add('ProductOption__variant--selected');

  const secondarySizeSelector = document.querySelector(
    `[data-secondary-size-variant-selector][data-value="${variant.option2}"]`,
  );
  if (secondarySizeSelector) secondarySizeSelector.classList.add('ProductOption__variant--selected');
};

// Sync sold out items
const syncSoldOutItemsMobileSticky = () => {
  document.querySelectorAll('[data-secondary-size-variant-selector]').forEach((el) => {
    el.classList.remove('ProductOption__variant--unavailable');
    el.classList.remove('ProductOption__variant--sold-out');
  });

  document.querySelectorAll('.ProductOption__variant--unavailable[data-size-variant-selector]').forEach((el) => {
    const val = el.getAttribute('data-value');
    const target = document.querySelector(`[data-secondary-size-variant-selector][data-value="${val}"]`);
    if (target) target.classList.add('ProductOption__variant--unavailable');
  });

  document.querySelectorAll('.ProductOption__variant--sold-out[data-size-variant-selector]').forEach((el) => {
    const val = el.getAttribute('data-value');
    const target = document.querySelector(`[data-secondary-size-variant-selector][data-value="${val}"]`);
    if (target) target.classList.add('ProductOption__variant--sold-out');
  });
};

/**
 * Re-fetch the QuickView gallery section for a new variant and re-initialize Gallery.
 * The cart edit modal uses the new scroll gallery while the legacy PDP keeps Swiper.
 */
const refetchQuickViewGallery = (variant, product_json) => {
  const galleryEl = document.querySelector('#QuickView.Modal--open #quickview-gallery');
  if (!galleryEl) return;

  const productPath = `/products/${product_json.handle}`;
  const sectionUrl = `${productPath}?variant=${variant.id}&sections=quickview-gallery-content`;

  fetch(sectionUrl)
    .then((response) => {
      if (!response.ok) throw new Error('QuickView gallery re-fetch failed');
      return response.json();
    })
    .then((data) => {
      const newHtml = getSectionInnerHTML(data['quickview-gallery-content'], '.shopify-section');
      if (newHtml) {
        galleryEl.innerHTML = newHtml;
        const galleryInstance = new Gallery({
          gallery: galleryEl,
          prefixClassName: 'pdp-c-gallery',
          features: { keydown: false, zoom: false },
        });
        galleryInstance.setObservers();
        setTimeout(() => {
          galleryInstance.init();
          if (galleryInstance.carousel) {
            galleryInstance.carousel.scrollTo({ left: 0 });
          }
        }, 0);
      }
    })
    .catch((error) => {
      console.error('QuickView gallery re-fetch error:', error);
    });
};

/**
 * Update the carousel images based on the selected variant
 * @param {*} variant - variant object
 * @param {*} product_json - product object
 * @returns {void}
 */
const updateCarouselImages = (variant, product_json) => {
  const variantImages = product_json.media;
  const variantBarcode = variant.barcode;
  let carouselMainHtml = '';
  let carouselThumbsHtml = '';

  if (product_json.type.includes('GIVEX')) {
    carouselMainHtml = `<div class="swiper-slide"><img class='ProductImages__image' data-product-images-slide src=${variant.featured_image.src}></div>`;
    carouselThumbsHtml = `<div class="ProductImages__thumbs-placeholder swiper-slide" data-product-images-thumb><img data-product-images-thumbs-slide src="${variant.featured_image.src}"></div>`;

    const carouselMain = document.querySelector('[data-product-images-carousel] .swiper-wrapper');
    const carouselThumbs = document.querySelector('[data-product-images-thumbs-wrapper] .swiper-wrapper');
    if (carouselMain) {
      carouselMain.innerHTML = carouselMainHtml;
    }
    if (carouselThumbs) {
      carouselThumbs.innerHTML = carouselThumbsHtml;
    }
  } else {
    /**
     * Site Performance Product Images Optimization
     * https://windsorfashions.atlassian.net/browse/WIN-6237
     * Instead of utilizing JS to update the product image, we will be using the Shopify Liquid to render the images. View the ProductImages.liquid for more details.
     */
    const altText = document.querySelector('[data-product-images-slide]').getAttribute('alt');

    if (!variantImages) {
      console.error('no variant images found');
      throw new Error('no variant images found');
    }

    const mediaArray = variantImages
      .map((media) => {
        const isVideo = media.media_type.includes('video');
        const fileN = isVideo ? media.preview_image.src : media.src;
        const splitV = isVideo ? 'preview_images/' : '/';

        const filename = fileN.split(splitV).pop();

        const parts = filename.split('_');
        const sortNumber = parseInt(parts[1] || '0', 10);

        return {
          sortNumber,
          mediaId: media.id,
        };
      })
      .sort((a, b) => a.sortNumber - b.sortNumber)
      .map((item) => `${item.sortNumber}:${item.mediaId}`);

    mediaArray.forEach((image) => {
      let parts = image.split(':');
      let mediaId = Number(parts[1]);
      image = variantImages.find((media) => media.id === mediaId);
      let fileSource = image.src;
      let splitValue = '/';
      const isVideo = image.media_type.includes('video');
      if (isVideo) {
        fileSource = image.preview_image.src;
        splitValue = 'preview_images/';
      }
      let url_slash_split = fileSource.split(splitValue);
      let url_last_section = url_slash_split[url_slash_split.length - 1];
      let url_style_number = url_last_section.split('_')[0];
      if (url_style_number == variantBarcode) {
        if (isVideo) {
          const videoSourceUrl = applyVideoFetchPrefix(image.sources[0].url, 'pdp');
          carouselMainHtml += `
          <div class="swiper-slide no-zoom" data-video-variant-barcode="${url_style_number}">
              <video playsinline="" id="productVideo" loop muted aria-label="Product video" autoplay poster="${fileSource}" data-video-variant-barcode="${url_style_number}" data-video-sort-order="${parts[0]}">
                <source src="${videoSourceUrl}" type="video/mp4">
                Your browser does not support the video tag.
              </video>
              <button id="playPauseButton" aria-label="Pause video">
                <span class="pause" aria-hidden="true">
                  <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 0.333334V9.66667H2.66667V0.333334H0ZM5.33333 0.333334V9.66667H8V0.333334H5.33333Z" fill="black"></path>
                  </svg>
                </span>
                <span class="play" aria-hidden="true">
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.666626 0.777802V13.2222L11.3333 7.00002L0.666626 0.777802Z" fill="black"></path>
                  </svg>
                </span>
                <span class="visually-hidden">Pause</span>
              </button>
          </div>
          `;
          carouselThumbsHtml += `
          <div class="ProductImages__thumbs-placeholder swiper-slide" data-video-variant-barcode="${url_style_number}">
            <video poster="${fileSource}" data-video-variant-barcode="${url_style_number}">
              <source src="${videoSourceUrl}" type="video/mp4">
            </video>
            <div class="icon-play">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.9998 0.666687C6.64784 0.666687 0.666504 6.64802 0.666504 14C0.666504 21.352 6.64784 27.3334 13.9998 27.3334C21.3518 27.3334 27.3332 21.352 27.3332 14C27.3332 6.64802 21.3518 0.666687 13.9998 0.666687ZM11.3332 18.6187V9.38135L19.3332 14L11.3332 18.6187Z" fill="white"></path>
              </svg>
            </div>
          </div>
          `;
        } else {
          let ext = fileSource.includes('.webp') ? '.webp' : '.jpg';
          let resized_image = fileSource.split(ext);
          const resizedImageWidth = 633;
          const resizedImageHeight = 715;
          let new_resized_url = resized_image.join(`_${resizedImageWidth}x${resizedImageHeight}_crop_center` + ext);
          const resizedThumbWidth = 137;
          const resizedThumbHeight = 161;
          let new_resized_thumbs = resized_image.join(`_${resizedThumbWidth}x${resizedThumbHeight}_crop_center` + ext);
          let zoom_url = resized_image.join('_1594x1800_crop_center' + ext);

          carouselMainHtml += `<div class="swiper-slide"><img class='ProductImages__image' alt="${altText}" data-zoom="${zoom_url}" data-product-images-slide src="${new_resized_url}" width=${resizedImageWidth} height=${resizedImageHeight}></div>`;
          carouselThumbsHtml += `<div class="ProductImages__thumbs-placeholder swiper-slide"  data-product-images-thumb><img data-product-images-thumbs-slide alt="${altText}" src="${new_resized_thumbs}"></div>`;
        }
      }
    });

    // update carousels
    const carouselMain = document.querySelector('[data-product-images-carousel] .swiper-wrapper');
    const carouselThumbs = document.querySelector('[data-product-images-thumbs-wrapper] .swiper-wrapper');

    if (carouselMain) {
      carouselMain.innerHTML = carouselMainHtml;
    }
    if (carouselThumbs) {
      carouselThumbs.innerHTML = carouselThumbsHtml;
    }
  }
};

/**
 * Handler for PDP videos
 * @param {*} variant - variant object
 */
const handlerPDPVideos = (variant) => {
  // Handle PDP videos
  const pdpContainer = document.querySelector('.ProductOverview');
  if (pdpContainer && window.PDPVideo) {
    if (!pdpContainer.pdpVideoInstance) {
      pdpContainer.pdpVideoInstance = new window.PDPVideo(document);
    }

    // Process videos for this variant
    pdpContainer.pdpVideoInstance.processVideosForVariant(variant.barcode || variant.sku);

    // Update video visibility first
    pdpContainer.pdpVideoInstance.updateVideoVisibility(variant.barcode || variant.sku, pdpContainer);

    // Listen for visibility update to reset slides
    const visibilityUpdateHandler = () => {
      const mainSwiperEl = pdpContainer.querySelector('.swiper-main');
      const thumbsSwiperEl = pdpContainer.querySelector('.swiper-thumbs');
      if (mainSwiperEl?.swiper && thumbsSwiperEl?.swiper) {
        // Update swipers to ensure all slides are registered
        mainSwiperEl.swiper.update();
        mainSwiperEl.swiper.updateSlides();
        mainSwiperEl.swiper.updateProgress();
        thumbsSwiperEl.swiper.update();
        thumbsSwiperEl.swiper.updateSlides();
        thumbsSwiperEl.swiper.updateProgress();

        // Reset to first slide
        mainSwiperEl.swiper.slideTo(0, 0, false);
        thumbsSwiperEl.swiper.slideTo(0, 0, false);

        // Update active classes manually
        const slides = thumbsSwiperEl.querySelectorAll('.swiper-slide');
        slides.forEach((slide, index) => {
          if (index === 0) {
            slide.classList.add('swiper-slide-active', 'swiper-slide-thumb-active');
          } else {
            slide.classList.remove('swiper-slide-active', 'swiper-slide-thumb-active');
          }
        });

        // Finally sync everything
        pdpContainer.pdpVideoInstance.updateCarousels();
      }

      // Remove the event listener
      pdpContainer.removeEventListener(window.PDPVideo.VIDEO_EVENTS.VIDEO_VISIBILITY_UPDATED, visibilityUpdateHandler);
    };

    pdpContainer.addEventListener(window.PDPVideo.VIDEO_EVENTS.VIDEO_VISIBILITY_UPDATED, visibilityUpdateHandler);

    // Listen for carousel updates to reinitialize zoom
    const carouselUpdateHandler = () => {
      if (typeof window.reinitializeZoom === 'function') {
        window.reinitializeZoom();
      }
      pdpContainer.removeEventListener(window.PDPVideo.VIDEO_EVENTS.CAROUSELS_UPDATED, carouselUpdateHandler);
    };
    pdpContainer.addEventListener(window.PDPVideo.VIDEO_EVENTS.CAROUSELS_UPDATED, carouselUpdateHandler);
  }

  // Handle QuickView videos
  const quickViewContainer = document.querySelector('.QuickView[aria-hidden="false"]');
  if (quickViewContainer && window.PDPVideo) {
    if (!quickViewContainer.pdpVideoInstance) {
      quickViewContainer.pdpVideoInstance = new window.PDPVideo(quickViewContainer);
    }

    // Process videos for this variant
    quickViewContainer.pdpVideoInstance.processVideosForVariant(variant.barcode || variant.sku);
  }
};

/**
 * It update/refresh images, thumbnails images, carousel, and swipe images and non user-visible values
 * each time customer swicthes between size and color selectors
 * @param {Object} variant - variant data
 * @param {Object} inventory_json - availability data
 * @param {Object} product_json - product data
 * @param {Boolean} firstTimeLoading - determine if it was reloaded or not
 * @param {String} option - it will store what customer selected either 'color' or 'size', it will internally address the rest of actions
 * @returns {void} - it'll return no data
 */
const updateSelectedVariant = (variant, inventory_json, product_json, firstTimeLoading, option) => {
  if (!variant) {
    return;
  }

  const quickViewModal = document.querySelector('#QuickView.Modal--open');
  const isBuiltWithFastSimon = quickViewModal?.getAttribute('data-built-with-fastsimon') === 'true';

  if (isBuiltWithFastSimon) {
    return;
  }

  // Store Constructor Tracking instance
  let cnstrcTracking;
  // it checks if Constructor Tracking instance already exists to not create a new one each time
  if (window.cnsQuickView && window.cnsQuickView.validateObj(quickViewModal)) {
    cnstrcTracking = window.cnsQuickView;
  } else {
    const cta = quickViewModal.querySelector('button[type="submit"]');
    cnstrcTracking = new ConstructorTracking({
      container: quickViewModal,
      isValidCta: cta?.getAttribute('data-add-to-cart') ? true : false,
    });
    window.cnsQuickView = cnstrcTracking;
  }

  // We will refresh images, carousel, etc. once option was 'color'. It is not
  // necessary to do it when switching between 'size' values.
  if (!firstTimeLoading && option === 'color') {
    const quickViewGallery = document.querySelector('#QuickView.Modal--open #quickview-gallery');

    if (quickViewGallery) {
      // The cart edit modal uses the new scroll gallery. Re-render it from the
      // section endpoint so the selected color's media is server-resolved.
      destroyZoom();
      updateColorValue(variant);

      const variantChangeEvent = new CustomEvent('variantChange', {
        detail: {
          variant: variant,
        },
      });
      document.dispatchEvent(variantChangeEvent);

      refetchQuickViewGallery(variant, product_json);
    } else {
      // Keep the legacy Swiper path for the PDP and older AJAX responses.
      destroyZoom();
      updateCarouselImages(variant, product_json);
      updateColorValue(variant);

      const variantChangeEvent = new CustomEvent('variantChange', {
        detail: {
          variant: variant,
        },
      });
      document.dispatchEvent(variantChangeEvent);

      initProductCarousels();
      setTimeout(() => handlerPDPVideos(variant), 500);

      if (typeof window.initProductCarousel === 'function') {
        window.initProductCarousel();
      }

      setTimeout(() => {
        if (typeof window.reinitializeZoom === 'function') {
          window.reinitializeZoom();
        }
      }, 500);
    }
  }

  // update size under the title
  option === 'size' && updateSizeValue(variant);

  // they aren't user-visible tasks. So they can be deferred
  setTimeout(() => {
    // Assign the current variant barcode
    replaceVariantBarcode(variant);

    // update the style number under the title
    updateStyleNumber(variant);

    // update low inventory message
    updateLowInvetoryMessage(variant, inventory_json);

    // Update Klarna widget value on variant update
    updateKlarnaPaymentValue(variant);

    // update clearance message and badge
    switchClearanceBadge(variant);

    // Replace the old sku and variant id with new ones
    replaceSkuAndVariantId(variant);

    if (firstTimeLoading) {
      const variantChange = new CustomEvent('variantChange');
      document.dispatchEvent(variantChange);
    }

    // Update the final sale message in PDP
    toggleFinalSaleMessage(variant, product_json);

    // Sync the sticky nav options
    syncStickyNavOptions(variant);

    // Sync secondary sticky nav options
    syncStickySecondaryNavOptions(variant);

    // Sync Sold Out or Unavailable status on mobile sticky
    syncSoldOutItemsMobileSticky();

    // Update Constructor Tracking data attributes
    cnstrcTracking.updateVariation(variant);
  }, 0);
};

// Initialize Klarna data on page load (exported for use in ProductOptions)
export const initKlarnaOnPageLoad = (variant) => {
  if (!variant) return;

  // Fetch initial Klarna data
  updateKlarnaPaymentValue(variant);
};

export default updateSelectedVariant;
